import React, { useEffect, useRef, useState } from 'react';
import Loader from '../common/Loader/Loader';
import { Trans, useTranslation } from 'react-i18next';
import API from '../../utils/ApiConfig';
import { ACCOUNT_GROUP_HIERARCHY, ACCOUNT_MEMBER_LIST } from '../../utils/ApiEndpoints';
import TreePM from '../common/TreePM/TreePM';
import DataTablePM from '../common/DataTablePM/DataTablePM';
import { Column } from 'primereact/column';
import AutomatedOnboardingFooter from '../AutomatedOnboardingFooter/AutomatedOnboardingFooter';

const AutomatedOnboardingAccountGroupMapping = props => {
  const { commonProps, activeStep, onActiveStepChange, onInitializationError, onDataSaveError, onNavigationError, onDataSaveNotification } = props;

  const LABEL_SEPARATOR = ' -- ';
  const NODE_TYPE_DATA_FOLDER = 'DATA_FOLDER';
  const NODE_TYPE_DATA_NODE = 'DATA_NODE';

  const [pageInitialized, setPageInitialized] = useState(false); //...flag indicates if the page was loaded properly (no error)
  const mappingModified = useRef(false); //...flag indicates if account group mapping was modified by user

  const [isLoading, setLoading] = useState(false);
  const [accountGroupTreeNodes, setAccountGroupTreeNodes] = useState(null);
  const [accountGroupTreeSelection, setAccountGroupTreeSelection] = useState([]);
  const [accountGroupTreeSelectionActiveNode, setAccountGroupTreeSelectionActiveNode] = useState(null);
  const [accountGroupTreeExpandedKeys, setAccountGroupTreeExpandedKeys] = useState({});

  const [accountMemberListNodes, setAccountMemberListNodes] = useState([]);
  const [accountMemberListSelection, setAccountMemberListSelection] = useState([]);

  const [accountAssignDisabled, setAccountAssignDisabled] = useState(false);
  const [accountUnassignDisabled, setAccountUnassignDisabled] = useState(null);
  const [nextPageDisabled, setNextPageDisabled] = useState(true); //...disable nextPage button till data is loaded and validated

  useEffect(() => {
    let errorInLoading = false;
    let loadingComponents = 2;
    setLoading(true);

    //...call backend to load free account members and convert the data to UI listbox model
    API.get(ACCOUNT_MEMBER_LIST, {})
      .then(data => {
        initializeAccountMemberList(data && data.data ? data.data : null);
      })
      .catch(err => {
        errorInLoading = true;
        onInitializationError(err);
        setTimeout(() => $('.p-filter-column .p-checkbox-box').focus(), 100);
      })
      .finally(() => {
        loadingComponents--;
        finishLoading(loadingComponents, errorInLoading);
      });

    //...call backend to load account groups and convert the data to UI tree model
    API.get(ACCOUNT_GROUP_HIERARCHY, {})
      .then(data => {
        initializeAccountGroupTree(data && data.data ? data.data : null);
      })
      .catch(err => {
        errorInLoading = true;
        onInitializationError(err);
      })
      .finally(() => {
        loadingComponents--;
        finishLoading(loadingComponents, errorInLoading);
      });
  }, []);

  useEffect(() => {
    checkButtonsStatus();
  }, [accountMemberListNodes, accountGroupTreeNodes, accountMemberListSelection, accountGroupTreeSelection, accountGroupTreeSelectionActiveNode]);

  /**
   * Finish loading if there is no more component to be loaded.
   * Sets the flag pageInitialized to True when page was already successful loaded or False if not (either there was an error or page is not fully loaded yet)
   *
   * @param loadingComponents Number of components to be loaded
   * @param errorInLoading Flag indicates if there was any issue during page load
   */
  const finishLoading = (loadingComponents, errorInLoading) => {
    if (loadingComponents <= 0) {
      if (!errorInLoading) {
        setPageInitialized(true);
      }
      setLoading(false);
    }
  };

  const { t } = useTranslation();
  const tc = (e, data) => (data ? t(`autoonboard_account_group_mapping:${e}`, data) : t(`autoonboard_account_group_mapping:${e}`));
  const tc_common = (e, data) => (data ? t(`common:${e}`, data) : t(`common:${e}`));

  /**
   * Map backend account member data to UI listbox model. There should be flat structure of account nodes which will be mapped to an array of listbox values.
   * Label of the node is composed by the node name and node description separated by a LABEL_SEPARATOR. In case there is no description, then it will be just the name (in case there is no name just description then it will be LABEL_SEPARATOR and description (' - description' by default) to distinguish description from name). Space is considered as valid content for name or description, empty string is considered as null.
   * Key for the node will be the composedKey if defined - if not, then ID becomes the key.
   * Incoming accountNode (backend entity) won't be persisted - it will be thrown away once it is converted to UI datamodel.
   * This method expects just one level of data (an array) in the input and it returns array as output.
   *
   * @param sourceAccountMemberData Source data (coming from API)
   */
  const initializeAccountMemberList = sourceAccountMembersData => {
    if (sourceAccountMembersData) {
      let listNodes = [];
      for (const sourceAccountNode of [].concat(sourceAccountMembersData)) {
        let composedLabel = sourceAccountNode.name ? sourceAccountNode.name : '';
        if (sourceAccountNode.description && sourceAccountNode.description.length > 0) {
          composedLabel = composedLabel + LABEL_SEPARATOR + sourceAccountNode.description;
        }
        const listNode = {
          id: sourceAccountNode.id,
          name: sourceAccountNode.name,
          key: sourceAccountNode.composedKey && sourceAccountNode.composedKey.length > 0 ? sourceAccountNode.composedKey : sourceAccountNode.id,
          label: composedLabel,
          nodeType: NODE_TYPE_DATA_NODE, //...node type cannot be anything else than NODE_TYPE_DATA_NODE
          dirty: false, //...indicates if the node was modified
        };

        listNodes.push(listNode);
      }

      setTimeout(() => {
        if (listNodes.length > 0) {
          $('.p-filter-column .p-checkbox-box').focus();
        } else {
          $('.pm-next-button').focus();
        }
      }, 100);

      setAccountMemberListNodes(sortArrayByLabel(listNodes));
    } else {
      setAccountMemberListNodes(null);
    }
  };

  /**
   * Map backend account group data to UI tree model. Root acts just as a wrapper and won't be displayed.
   *
   * @param sourceTreeData Source data (coming from API)
   */
  const initializeAccountGroupTree = sourceTreeData => {
    if (sourceTreeData) {
      const treeRoot = mapAccountNodeToTreeNode(sourceTreeData);
      setAccountGroupTreeNodes(treeRoot.children);
    } else {
      setAccountGroupTreeNodes(null);
    }
  };

  /**
   * Map a backend data node (including subnodes) to UI tree node. Label of the node is composed by the node name and node description separated by a LABEL_SEPARATOR. In case there is no description, then it will be just the name (in case there is no name just description then it will be LABEL_SEPARATOR and description (' - description' by default) to distinguish description from name). Space is considered as valid content for name or description, empty string is considered as null.
   * Key for the node will be the composedKey if defined - if not, then ID becomes the key.
   * Each folder level should indicate how many leaf nodes are under it - this is calculated from bottom to top.
   * Incoming accountNode (backend entity) won't be persisted - it will be thrown away once it is converted to UI datamodel.
   * This method is called recursively to map the whole structure.
   *
   * @param accountNode Current node to be mapped
   * @param parentUINode UI Tree node that should be parent for the account node (null by default makes the node root)
   * @param topLevelNode Flag indicates if nodes should be marked as top level nodes (the first level nodes), defaults to true when called without parameters
   * @returns UI Tree model for the node
   */
  const mapAccountNodeToTreeNode = (accountNode, parentUINode = null, topLevelNode = true) => {
    if (!accountNode) {
      return null;
    }
    let composedLabel = accountNode.name ? accountNode.name : '';
    if (accountNode.description && accountNode.description.length > 0) {
      composedLabel = composedLabel + LABEL_SEPARATOR + accountNode.description;
    }
    const treeNode = {
      id: accountNode.id,
      name: accountNode.name,
      key: accountNode.composedKey && accountNode.composedKey.length > 0 ? accountNode.composedKey : accountNode.id,
      label: composedLabel,
      nodeType: accountNode.nodeType,
      parent: parentUINode,
      children: [],
      leafNodesCount: 0,
      topLevelNode: topLevelNode,
      dirty: false, //...indicates if the node was modified
    };
    if (accountNode.children) {
      let containsDirectDataNode = false;
      for (const childNode of accountNode.children) {
        let childTreeNode = mapAccountNodeToTreeNode(childNode, treeNode, parentUINode == null);
        treeNode.children.push(childTreeNode);
        if (childNode.nodeType == NODE_TYPE_DATA_NODE) {
          containsDirectDataNode = true;
          treeNode.leafNodesCount++;
        } else {
          treeNode.leafNodesCount = treeNode.leafNodesCount + childTreeNode.leafNodesCount;
        }
      }
      if (containsDirectDataNode) {
        treeNode.children = sortArrayByLabel(treeNode.children);
      }
    }

    return treeNode;
  };

  /**
   * Sort an array by the field 'label' alphabetically.
   */
  const sortArrayByLabel = nodes => {
    return nodes.sort((a, b) => a.label.localeCompare(b.label));
  };

  /**
   * Check status (enabled/disabled) aof page buttons and update it properly.
   */
  const checkButtonsStatus = () => {
    checkPageControlButtonsStatus();
    checkAccountMoveButtonsStatus();
  };

  /**
   * Set 'disabled' flag for page control buttons as Next, Save etc. Do not consider <code>pageInitialized</code> here as it will automatically disable buttons directly in the footer.
   */
  const checkPageControlButtonsStatus = () => {
    setNextPageDisabled(accountMemberListNodes && accountMemberListNodes.length > 0);
  };

  /**
   * Set the 'disabled' flag for the account assign and unassign button.
   */
  const checkAccountMoveButtonsStatus = () => {
    const assignBtnEnabled =
      accountMemberListSelection &&
      accountMemberListSelection.length > 0 &&
      accountGroupTreeSelectionActiveNode &&
      accountGroupTreeSelectionActiveNode.nodeType == NODE_TYPE_DATA_FOLDER;
    const unassignBtnEnabled = accountGroupTreeSelectionActiveNode && accountGroupTreeSelectionActiveNode.nodeType == NODE_TYPE_DATA_NODE;
    setAccountAssignDisabled(!assignBtnEnabled);
    setAccountUnassignDisabled(!unassignBtnEnabled);
  };

  /**
   * Remove selected account member node from the tree. Only the leaf (DATA_NODE) can be removed. If it is not appropriate type, error will be displayed.
   * Tag the node (and whole account mapping) as dirty indicating that it was changed and should be saved to database.
   * Check and adjust availability of buttons (assign/unassign and control buttons as Next page) - all of them may be changed as the selection and amount of unassigned nodes was changed. The check will be triggered automatically by watcher in useEffect.
   */
  const removeAccountMemberNodeFromTree = () => {
    if (!accountGroupTreeSelectionActiveNode || accountGroupTreeSelectionActiveNode.nodeType != NODE_TYPE_DATA_NODE) {
      setErrorMessage(tc('ACCOUNT_GROUP_MAPPING_INCORRECT_ASSIGNMENT')); //...this should never happen - button should be disabled
      setShowErrorMessage(true);
    } else {
      const parentTreeNode = accountGroupTreeSelectionActiveNode.parent;
      const siblingsNodes = parentTreeNode ? parentTreeNode.children : accountGroupTreeNodes;
      accountMemberListNodes.push(accountGroupTreeSelectionActiveNode);
      const accountGroupTreeNodeIndex = siblingsNodes.findIndex(element => element.key == accountGroupTreeSelectionActiveNode.key);
      siblingsNodes.splice(accountGroupTreeNodeIndex, 1);
      accountGroupTreeSelectionActiveNode.dirty = true;
      mappingModified.current = true;

      if (parentTreeNode) {
        adjustLeafNodesCount(parentTreeNode, -1);
        if (siblingsNodes.length == 0) {
          if (accountGroupTreeExpandedKeys[parentTreeNode.key]) {
            delete accountGroupTreeExpandedKeys[parentTreeNode.key]; //...collapse empty group
          }
        }
      }
      accountGroupTreeSelectionActiveNode.parent = null;
      sortArrayByLabel(accountMemberListNodes);
      sortArrayByLabel(siblingsNodes);

      setAccountMemberListNodes([...accountMemberListNodes]);
      setAccountGroupTreeNodes(parentTreeNode ? touchTreeStructureUpwards(parentTreeNode).children : [...accountGroupTreeNodes]);
      setAccountGroupTreeSelectionActiveNode(null);
      setAccountGroupTreeSelection([]);
    }
  };

  /**
   * Add a node as child of currently selected node if and only if the selected node is of appropriate type (DATA_FOLDER). If not is not appropriate type or if there is no account member (unassigned account) selected, error will be displayed.
   * Tag the node(s) (and whole account mapping) as dirty indicating that it was changed and should be saved to database.
   * Check and adjust availability of buttons (assign/unassign and control buttons as Next page) - all of them may be changed as the selection and amount of unassigned nodes was changed. The check will be triggered automatically by watcher in useEffect.
   */
  const addAccountMemberNodeToTree = () => {
    if (
      !accountGroupTreeSelectionActiveNode ||
      accountGroupTreeSelectionActiveNode.nodeType != NODE_TYPE_DATA_FOLDER ||
      !accountMemberListSelection ||
      accountMemberListSelection.length == 0
    ) {
      setErrorMessage(tc('ACCOUNT_GROUP_MAPPING_INCORRECT_ASSIGNMENT')); //...this should never happen - button should be disabled
      setShowErrorMessage(true);
    } else {
      for (const accountMember of accountMemberListSelection) {
        accountGroupTreeSelectionActiveNode.children.push(accountMember);
        const accountMemberListNodeIndex = accountMemberListNodes.findIndex(element => element.key == accountMember.key);
        accountMemberListNodes.splice(accountMemberListNodeIndex, 1);
        accountMember.parent = accountGroupTreeSelectionActiveNode;
        accountMember.dirty = true;
      }
      adjustLeafNodesCount(accountGroupTreeSelectionActiveNode, accountMemberListSelection.length);
      mappingModified.current = true;

      if (!accountGroupTreeExpandedKeys[accountGroupTreeSelectionActiveNode.key]) {
        accountGroupTreeExpandedKeys[accountGroupTreeSelectionActiveNode.key] = true; //...expand the group
      }
      sortArrayByLabel(accountMemberListNodes);
      sortArrayByLabel(accountGroupTreeSelectionActiveNode.children);

      setAccountMemberListSelection([]);
      setAccountMemberListNodes([...accountMemberListNodes]);
      setAccountGroupTreeNodes(touchTreeStructureUpwards(accountGroupTreeSelectionActiveNode).children);
    }
  };

  /**
   * Adjust leafNodesCount for a hierarchy from the baseNode up to the root node.
   * adjustCount (possibly positive or negative) will be added to leafNodesCount starting with baseNode and all the way up.
   */
  const adjustLeafNodesCount = (baseNode, adjustCount) => {
    baseNode.leafNodesCount = baseNode.leafNodesCount + adjustCount;
    if (baseNode.parent) {
      adjustLeafNodesCount(baseNode.parent, adjustCount);
    }
  };

  /**
   * Touch the Account Groups Tree from given base node upwards. Make sure that each node in the structure will be replaced by its
   * duplicate, so React may recognize that there was a change. When there are child nodes in the node, the parent link has to be updated.
   *
   * @param baseNode Base node - touch the structure from this node (included) up to the root
   * @return Root node of touched (updated) structure
   */
  const touchTreeStructureUpwards = baseNode => {
    let nodeDuplicate = {
      ...baseNode,
      children: [...baseNode.children],
    };
    for (const treeNodeMember of nodeDuplicate.children) {
      treeNodeMember.parent = nodeDuplicate; //...update parents to keep the structure
    }
    if (baseNode.parent) {
      //...put duplicate element to the children in parent object (removing the old object)
      let parentTreeNode = baseNode.parent;
      const nodeIndex = parentTreeNode.children.findIndex(element => element.key == baseNode.key);
      parentTreeNode.children.splice(nodeIndex, 1, nodeDuplicate);
      return touchTreeStructureUpwards(parentTreeNode);
    }
    return nodeDuplicate;
  };

  /**
   * Get real Account Group mapping tree node according to what UI sends as a selection.
   * This is quite nasty hack to handle even nastier behavior of UI Tree component. When a filter is applied on the UI tree,
   * it reduces the nodes to only those that fits the filter criteria. All the node attributes are the same, but the array <code>children</code>
   * is reduced to only those children that meets the criteria. That means the node returned from UI as selection may have smaller amount of
   * children than the real node.
   * This method gets the real node from the tree structure and replace the UI node with the rela node (which has the real array of children, regardless if they are currently visible or not).
   * That is achieved by going there and back - get parent and then get child from parent - as parent link is not known to UI, it still points to the original structure.
   */
  const getRealAccountGroupTreeNodeForSelection = uiSelection => {
    let realNodeSelection = null;
    if (uiSelection && uiSelection.parent) {
      realNodeSelection = uiSelection.parent.children.find(element => element.key == uiSelection.key);
    }
    return realNodeSelection ? realNodeSelection : uiSelection;
  };

  // footer
  const save = (saveType, callbackfn) => {
    if (!pageInitialized) {
      onDataSaveError(null); //...unexpected error, call of this method should not be possible
      return;
    }

    setLoading(true);
    let mappingStructure = {};
    addAccountsToMappingStructure(accountGroupTreeNodes, mappingStructure);

    API.put(ACCOUNT_GROUP_HIERARCHY, {
      saveType: saveType,
      accountGroupMapping: mappingStructure,
    })
      .then(() => {
        resetDirtyFlags();

        callbackfn();
      })
      .catch(err => {
        onDataSaveError(err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const footerBtnInfo = {
    backBtn: { disabled: false, showBtn: true, click: save },
    nextBtn: { disabled: nextPageDisabled, showBtn: true, click: save },
    saveBtn: { disabled: false, showBtn: true, click: save },
    saveExitBtn: { disabled: false, showBtn: true, click: save },
  };

  /**
   * Convert nodes from the UI hierarchy tree to account mapping structure and add them to the mapping structure entity provided.
   * This method is called in recurse for each node.
   * Note that the account group name is used, not the id. Backend is using name (value) instead of ID for whatever reason.
   *
   * @param treeNodes Array of one level of nodes from hierarchical tree - these nodes and all subnodes will be processed
   * @param mappingStructure Mapping structure to which the converted nodes will be added
   */
  const addAccountsToMappingStructure = (treeNodes, mappingStructure) => {
    if (treeNodes && treeNodes.length > 0) {
      for (const node of treeNodes) {
        if (node.nodeType == NODE_TYPE_DATA_NODE) {
          mappingStructure[node.id] = node.parent.name;
        }
        if (node.children) {
          addAccountsToMappingStructure(node.children, mappingStructure);
        }
      }
    }
  };

  /**
   * Reset dirty flags in the account structures coming as input parameter. Dirty flag will be set to false.
   * Call this method with <code>null</null> (or without parameter) in order to process both the group tree and unassigned list and also turn off the global flag indicating that mapping has been changed. This should happen mainly when data was successfully saved.
   *
   * @param nodes Nodes that should be reset (by default null to process all nodes) - these nodes and all subnodes will be processed
   */
  const resetDirtyFlags = (nodes = null) => {
    if (!nodes) {
      resetDirtyFlags([].concat(accountGroupTreeNodes));
      resetDirtyFlags([].concat(accountMemberListNodes));
      mappingModified.current = false;
    } else {
      if (nodes.length > 0) {
        for (const node of nodes) {
          if (node.dirty) {
            node.dirty = false;
          }
          if (node.children) {
            resetDirtyFlags(node.children);
          }
        }
      }
    }
  };

  /**
   * Render node in account group tree. Make sure to use appropriate icon for the node.
   */
  const accountGroupTreeNodeTemplateRenderer = (node, options) => {
    let iconClass;
    if (node.nodeType == NODE_TYPE_DATA_NODE) {
      iconClass = 'pi account-group-data-node-icon';
    } else if (node.nodeType == NODE_TYPE_DATA_FOLDER) {
      if (options.expanded) {
        iconClass = 'pi account-group-data-folder-open-icon';
      } else {
        iconClass = 'pi account-group-data-folder-closed-icon';
      }
    } else {
      iconClass = 'pi account-group-structure-node-icon';
    }

    let nodeCssClass = options.className;
    let fullNodeLabel = node.label;
    if (node.topLevelNode || (node.leafNodesCount && node.leafNodesCount > 0)) {
      nodeCssClass = nodeCssClass + ' account-group-data-folder-with-data';
      if (node.leafNodesCount && node.leafNodesCount > 0) {
        fullNodeLabel = fullNodeLabel + ' (' + node.leafNodesCount + ')';
      }
    }

    return (
      <span className={nodeCssClass}>
        <span className={iconClass} />
        {fullNodeLabel}
      </span>
    );
  };

  return (
    <div className="accountGroupMappingContainer">
      {isLoading ? <Loader /> : ''}

      <div className="automated-onboarding-title title">{tc('ACCOUNT_GROUP_MAPPING_TITLE')}</div>
      <div className="automated-onboarding-subtitle">
        <Trans t={tc} i18nKey="ACCOUNT_GROUP_MAPPING_SUBTITLE"></Trans>
      </div>
      <div className="accountGroupMappingGrid">
        <div className="accountMemberListColumn">
          <div className="automated-onboarding-title title">{tc('ACCOUNT_GROUP_MAPPING_SECTION_MEMBERS')}</div>
          <div className="accountMemberListPanel">
            <DataTablePM
              className="accountMemberList"
              value={accountMemberListNodes}
              selection={accountMemberListSelection}
              onSelectionChange={e => setAccountMemberListSelection(e.value)}
              showCheckbox={true}
              columns={[
                <Column
                  field="label"
                  key="0"
                  filter
                  filterMatchMode="contains"
                  filterPlaceholder={tc_common('FILTER_SEARCHFIELD_PLACEHOLDERTEXT')}
                />,
              ]}></DataTablePM>
          </div>
        </div>
        <div className="accountMoveButtonsColumn">
          <div>
            <button disabled={accountAssignDisabled} className="btn btn-outline btn-primary" onClick={addAccountMemberNodeToTree}>
              <i className="fa fa-angle-double-right"></i>
            </button>
            <button disabled={accountUnassignDisabled} className="btn btn-outline btn-primary" onClick={removeAccountMemberNodeFromTree}>
              <i className="fa fa-angle-double-left"></i>
            </button>
          </div>
        </div>
        <div className="accountGroupTreeColumn">
          <div className="automated-onboarding-title title">{tc('ACCOUNT_GROUP_MAPPING_SECTION_GROUPS')}</div>
          <div className="accountGroupTreePanel">
            <TreePM
              className="accountGroupTree"
              value={accountGroupTreeNodes}
              selectionMode="single"
              // metaKeySelection={true}
              // dragdropScope="accountGroupTreeNodesDragDrop" onDragDrop={event => setAccountGroupTreeNodes(event.value)}
              selectionKeys={accountGroupTreeSelection}
              onSelect={e => setAccountGroupTreeSelectionActiveNode(getRealAccountGroupTreeNodeForSelection(e.node))}
              onUnselect={e => setAccountGroupTreeSelectionActiveNode([])}
              onSelectionChange={e => setAccountGroupTreeSelection(e.value)}
              expandedKeys={accountGroupTreeExpandedKeys}
              onToggle={e => setAccountGroupTreeExpandedKeys(e.value)}
              nodeTemplate={accountGroupTreeNodeTemplateRenderer}
              filter={true}
              filterMode="strict"
              filterPlaceholder={tc_common('FILTER_SEARCHFIELD_PLACEHOLDERTEXT')}></TreePM>
          </div>
        </div>
      </div>

      <AutomatedOnboardingFooter
        commonProps={commonProps}
        btnInfo={footerBtnInfo}
        activeStep={activeStep}
        pageInitialized={pageInitialized}
        onActiveStepChange={s => onActiveStepChange(s)}
        onNavigationError={e => onNavigationError(e)}
        onDataSaveNotification={e => onDataSaveNotification(e)}
      />
    </div>
  );
};

export default AutomatedOnboardingAccountGroupMapping;